import { React, useEffect, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import * as bootstrap from 'bootstrap';
import { withdrawlServices } from '../services/WithdrawlServices';

const Withdrawal = () => {
    const [withdrawData, setWithdrawData] = useState([]);
    const [amount, setAmount] = useState('');
    const [walletAddress, setWalletAddress] = useState('');
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [showModal, setShowModal] = useState(false);
    const modalRef = useRef(null);
    
    const encodedData = localStorage.getItem('user');
    const userData = encodedData ? JSON.parse(decodeURIComponent(encodedData)) : {};
    const userId = localStorage.getItem('id');

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    // Function to fetch withdrawal data
    const fetchWithdrawData = async (page = 1, size = perPage) => {
        setLoading(true);
        try {
            // Assuming your API supports pagination parameters
            const response = await withdrawlServices.getWithdrawls(userId, { page, limit: size });
            if (response.status === 200) {
                const data = response.data.data;
                setWithdrawData(data.withdrawals || data); // Adjust based on your API response structure
                setTotalRows(data.total || data.length); // Set total count for pagination
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchWithdrawData();
        }
    }, [userId]);

    const columns = [
        {
            name: 'Username',
            selector: row => row.username,
            sortable: true,
            grow: 1,
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: true,
            grow: 2,
        },
        {
            name: 'Wallet Address',
            selector: row => row.wallet_address,
            sortable: true,
            width: '150px',
        },
        {
            name: 'Phone Number',
            selector: row => row.phoneNumber,
            width: '130px',
        },
        {
            name: 'Amount',
            selector: row => formatCurrency(row.amount),
            sortable: true,
            width: '120px',
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            cell: row => (
                row.status === 'pending' ? (
                    <span className="badge bg-warning">Pending</span>
                ) : row.status === 'approved' ? (
                    <span className="badge bg-success">Approved</span>
                ) : row.status === 'rejected' ? (
                    <span className="badge bg-danger">Rejected</span>
                ) : row.status === 'completed' ? (
                    <span className="badge bg-info">Completed</span>
                ) : (
                    <span className="badge bg-secondary">Pending</span>
                )
            ),
            width: '120px',
        },
        {
            name: 'Requested At',
            selector: row => formatDate(row.createdAt),
            sortable: true,
            width: '130px',
        },
        {
            name: 'Updated At',
            selector: row => formatDate(row.updatedAt),
            sortable: true,
            width: '130px',
        },
    ];

    const handlePageChange = (page) => {
        fetchWithdrawData(page, perPage);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        fetchWithdrawData(page, newPerPage);
    };

    const resetForm = () => {
        setAmount('');
        setWalletAddress('');
    };

    const openModal = () => {
        resetForm();
        setShowModal(true);
        // Use Bootstrap modal programmatically
        setTimeout(() => {
            const modalElement = modalRef.current;
            if (modalElement) {
                const modal = new bootstrap.Modal(modalElement, {
                    backdrop: 'static',
                    keyboard: false
                });
                modal.show();
            }
        }, 100);
    };

    const closeModal = () => {
        const modalElement = modalRef.current;
        if (modalElement) {
            const modalInstance = bootstrap.Modal.getInstance(modalElement);
            if (modalInstance) {
                modalInstance.hide();
            }
        }
        setShowModal(false);
        
        // Clean up any remaining backdrops
        setTimeout(() => {
            const backdrops = document.querySelectorAll('.modal-backdrop');
            backdrops.forEach(backdrop => backdrop.remove());
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
        }, 300);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!amount.trim() || !walletAddress.trim()) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (isNaN(amount) || parseFloat(amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        try {
            const data = {
                user_id: userId,
                username: userData?.fullName,
                email: userData?.email,
                wallet_address: walletAddress.trim(), // make sure backend expects wallet_address not account_no
                phoneNumber: userData?.phoneNumber,
                amount: parseFloat(amount),
            };

            const response = await withdrawlServices.addWithdrawlRequest(data);

            if (response.status === 201) {
                toast.success('Request generated successfully. Please wait for the further confirmation!');
                closeModal();
                fetchWithdrawData();
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            } else {
                toast.error(response.data.message || 'Failed to create withdrawal request');
            }
        } catch (error) {
            const serverMessage = error.response?.data?.message;

            if (serverMessage === 'Insufficient balance for withdrawal request') {
                toast.error('You do not have enough balance to make this withdrawal.');
            } else {
                const errorMessage = serverMessage || 'There are some errors in the fields. Please check again.';
                toast.error(errorMessage);
            }
        }
    };


    // Clean up on component unmount
    useEffect(() => {
        return () => {
            // Clean up any remaining backdrops on unmount
            const backdrops = document.querySelectorAll('.modal-backdrop');
            backdrops.forEach(backdrop => backdrop.remove());
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
        };
    }, []);

    const customStyles = {
        rows: {
            style: {
                minHeight: '56px',
            },
        },
        headCells: {
            style: {
                paddingLeft: '8px',
                paddingRight: '8px',
                fontWeight: 'bold',
                backgroundColor: '#f8f9fa',
            },
        },
        cells: {
            style: {
                paddingLeft: '8px',
                paddingRight: '8px',
            },
        },
    };

    return (
        <>
            <div className="body d-flex py-3">
                <div className="container-xxl">
                    <div className="row align-items-center">
                        <div className="border-0 mb-4">
                            <div className="card-header py-3 no-bg bg-transparent d-flex align-items-center px-0 justify-content-between border-bottom flex-wrap">
                                <h3 className="fw-bold mb-0">Withdrawals</h3>
                                <button 
                                    className="btn btn-primary step-btn" 
                                    onClick={openModal}
                                    type="button"
                                >
                                    + Add New
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="row align-item-center">
                        <div className="col-md-12">
                            <div className="card mb-3">
                                <div className="card-header py-3 bg-transparent border-bottom-0">
                                    <h6 className="mb-0 fw-bold">Withdrawal Requests</h6>
                                </div>
                                <div className="card-body">
                                    <DataTable
                                        columns={columns}
                                        data={withdrawData}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        paginationDefaultPage={1}
                                        paginationPerPage={perPage}
                                        paginationRowsPerPageOptions={[5, 10, 20, 50]}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onChangePage={handlePageChange}
                                        progressPending={loading}
                                        highlightOnHover
                                        striped
                                        responsive
                                        customStyles={customStyles}
                                        noDataComponent={
                                            <div className="text-center py-4">
                                                <p className="mb-0">No withdrawal requests found</p>
                                            </div>
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="addWithdrawal" tabIndex="-1" aria-hidden="true" ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold" id="expeditLabel1111">New Withdrawal Request</h5>
                            <button type="button" className="btn-close" onClick={closeModal} aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="deadline-form">
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-3 mb-3">
                                        <div className="col-sm-6">
                                            <label htmlFor="item100" className="form-label">Full Name</label>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                id="item100" 
                                                value={userData?.fullName || ''}
                                                disabled
                                            />
                                        </div>
                                        <div className="col-sm-6">
                                            <label htmlFor="mailid" className="form-label">Email</label>
                                            <input 
                                                type="email" 
                                                className="form-control" 
                                                id="mailid" 
                                                value={userData?.email || ''}
                                                disabled
                                            />
                                        </div>
                                        <div className="col-sm-6">
                                            <label htmlFor="phone" className="form-label">Phone Number</label>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                id="phone" 
                                                value={userData?.phoneNumber || ''}
                                                disabled
                                            />
                                        </div>
                                        <div className="col-sm-6">
                                            <label htmlFor="account_no" className="form-label">
                                                Wallet Address <span className="text-danger">*</span>
                                            </label>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                id="wallet_address"
                                                value={walletAddress}
                                                onChange={(e) => setWalletAddress(e.target.value)}
                                                required
                                                placeholder="Enter Wallet address"
                                            />
                                        </div>
                                        <div className="col-sm-12">
                                            <label htmlFor="amount" className="form-label">
                                                Withdrawal Amount <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text">$</span>
                                                <input 
                                                    type="number" 
                                                    className="form-control" 
                                                    id="amount"
                                                    value={amount}
                                                    onChange={(e) => setAmount(e.target.value)}
                                                    required
                                                    min="0.01"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                            <small className="form-text text-muted">
                                                Enter the amount you wish to withdraw
                                            </small>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                Cancel
                            </button>
                            <button 
                                onClick={handleSubmit} 
                                type="button" 
                                className="btn btn-primary"
                                disabled={!amount.trim() || !walletAddress.trim()}
                            >
                                Submit Request
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Withdrawal;